import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { Plus, Users, Search } from "lucide-react"

import { APP_NAME } from "@/lib/appConfig"
import { useTenants } from "@/features/tenants/hooks/useTenants"
import { TenantCard } from "@/features/tenants/components/TenantCard"
import { TenantForm } from "@/features/tenants/components/TenantForm"
import { PageHeader } from "@/components/shared/PageHeader"
import { EmptyState } from "@/components/shared/EmptyState"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { PlanLimitBanner } from "@/components/shared/PlanLimitBanner"
import { PullToRefresh } from "@/components/shared/PullToRefresh"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { usePlan } from "@/hooks/usePlan"

export default function TenantsPage() {
  const [searchParams] = useSearchParams()
  const [sheetOpen, setSheetOpen] = useState(() => searchParams.get("new") === "1")
  const [search, setSearch] = useState("")
  const { data: tenants, isLoading, isError, refetch } = useTenants()
  const { checkLimit } = usePlan()

  useEffect(() => {
    const prev = document.title
    document.title = `Tenants | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  const stats = {
    total: tenants?.length || 0,
    withLease: tenants?.filter((t) => t.active_lease).length || 0,
    noLease: tenants?.filter((t) => !t.active_lease).length || 0,
  }

  const count = tenants?.length ?? 0
  const { allowed, limit } = checkLimit("tenants", count)

  const filtered = tenants?.filter((t) =>
    t.full_name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Tenants"
        action={
          <Button
            size="sm"
            className="h-9 rounded-xl gap-1"
            onClick={() => setSheetOpen(true)}
            disabled={!allowed}
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        }
      />

      <PullToRefresh onRefresh={async () => { await refetch() }} className="flex-1 pb-nav">
        <div className="px-4 pt-6 pb-2">
          {!allowed && (
            <div className="mb-4">
              <PlanLimitBanner feature="tenants" limit={limit} current={count} />
            </div>
          )}
          {/* Top-Level Stats */}
          {!isLoading && !isError && tenants && tenants.length > 0 && (
            <div className="mb-4">
              <div className="flex bg-card/60 backdrop-blur-xl border border-border/50 rounded-2xl shadow-soft divide-x divide-border/50 overflow-hidden">
                <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">Total Tenants</span>
                  <span className="text-xl font-extrabold text-foreground">{stats.total}</span>
                </div>
                <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center bg-primary/5">
                  <span className="text-xs font-bold text-primary/80 uppercase tracking-widest mb-1">Active Leases</span>
                  <span className="text-xl font-extrabold text-primary">{stats.withLease}</span>
                </div>
                <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">No Lease</span>
                  <span className="text-xl font-extrabold text-foreground">{stats.noLease}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tenants..."
                className="pl-9 h-10 rounded-xl"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {isLoading && <ListSkeleton count={4} />}

        {isError && (
          <div className="flex flex-col items-center py-16 px-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Failed to load tenants.
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !isError && tenants?.length === 0 && (
          <EmptyState
            icon={<Users className="h-9 w-9 text-muted-foreground" />}
            title="No tenants yet"
            description="Add your first tenant to keep track of who's renting your properties."
            action={{ label: "Add Tenant", onClick: () => setSheetOpen(true) }}
          />
        )}

        {!isLoading && !isError && filtered && filtered.length === 0 && search && (
          <div className="flex flex-col items-center py-16 px-8 text-center">
            <p className="text-sm text-muted-foreground">
              No tenants match "{search}"
            </p>
          </div>
        )}

        {!isLoading && !isError && filtered && filtered.length > 0 && (
          <div className="px-4 pt-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((tenant) => (
              <TenantCard key={tenant.id} tenant={tenant} />
            ))}
          </div>
        )}
      </PullToRefresh>

      <TenantForm
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onSuccess={() => setSheetOpen(false)}
      />
    </div>
  )
}
